import React, { ReactNode, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLeftPanel from '../Common/AuthLeftPanel';
import { useFormik } from 'formik';
import { resetPasswordInitialValues, resetPasswordSchema } from 'constants/schemaValidations';
import { ResetPasswordInterface } from 'constants/interfaces';
import { useResetPasswordMutation } from 'services';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import BackBtn from 'components/Common/BackBtn';

const ResetPassword: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const userType = searchParams.get('userType');
	const resetToken = searchParams.get('token');

	// mutation
	const { mutateAsync: resetPasswordwordMutation, isPending } = useResetPasswordMutation();

	const [showPassword, setShowPassword] = useState({
		password: false,
		confirmPassword: false,
	});
	const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
		setShowPassword((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const formik = useFormik({
		initialValues: { ...resetPasswordInitialValues },
		validationSchema: resetPasswordSchema(translate),
		onSubmit: async (values) => {
			resetPasswordwordMutation({ ...values, token: resetToken })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
						navigate(`/login?userType=${userType}`);
					}
				})
				.catch((err: any) => {});
		},
	});

	const renderError = <T extends keyof ResetPasswordInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			<div className="auth-left-content">
				<AuthLeftPanel />
			</div>
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<BackBtn />
				<div className="auth-form mb-0 mt-5 mx-3 my-5 m-md-5">
					<div className="auth-form-heading text-center">
						<h2 className="mb-3">
							{translate('PlaceHolder.reset')}{' '}
							<span className="fw-bold">
								<br></br>
								{translate('PlaceHolder.password')}!
							</span>
						</h2>
						<p className="mb-0">{translate('Auth.authHeadingResetPassword')}</p>
					</div>
					<div className="auth-form-fields">
						<Form className="form" onSubmit={formik.handleSubmit}>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.newPassword')}</Form.Label>
								<Form.Control
									type={showPassword.password ? 'text' : 'password'}
									name="password"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.password}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword.password ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility('password')}
								></i>
								{renderError('password')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.confirmPassword')}</Form.Label>
								<Form.Control
									type={showPassword.confirmPassword ? 'text' : 'password'}
									name="confirmPassword"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.confirmPassword}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword.confirmPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility('confirmPassword')}
								></i>
								{renderError('confirmPassword')}
							</Form.Group>
							<div className="form-btn">
								<Button
									className="filled-btn rounded-pill w-100"
									type="submit"
									disabled={isPending}
								>
									{translate('Common.resetPassword')}
								</Button>
							</div>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ResetPassword;
